import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { formatStorageDisplayString } from '@shared-utils';
import toast from 'react-hot-toast';

export default function SubscriptionStatusPage() {
  const [subscription, setSubscription] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    syncConfigAndUsage();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);

      // Call Admin API to get subscription for current tenant
      const [subRes, usageRes] = await Promise.all([
        api.get('/subscription'),  // Gets subscription details
        api.get('/subscription/usage')  // Gets usage and quotas
      ]);

      setSubscription(subRes.subscription);
      setUsage(usageRes);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncConfigAndUsage = async (): Promise<void> => {
    try {
      setSyncing(true);
      setError(null);
      await api.triggerConfigAndUsageSync();
      // toast.success('Subscription configuration and usage synced successfully');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sync configuration and usage';
      // setError(errorMessage);
      toast.error(errorMessage);
      console.error('Sync error:', err);
    } finally {
      setSyncing(false);
      await loadSubscriptionStatus();
    }
  };

  if (loading || syncing) {
    return <div className="p-6">Loading subscription...</div>;
  }

  if (error || !subscription) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="font-semibold text-yellow-800">No Active Subscription</p>
          <p className="text-yellow-700">
            Your organization does not have an active subscription.
            Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-status-page p-6">
      <h1 className="text-3xl font-bold mb-6">Subscription Status</h1>

      {/*<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">*/}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-semibold">{subscription.packageName}</h2>
                <p className="text-gray-600 capitalize">{subscription.billingCycle} Billing</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                subscription.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                subscription.status === 'overdue' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {subscription.status.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <p className="text-sm text-gray-600">Renewal Date</p>
                <p className="text-lg font-semibold">
                  {subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : ''}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Amount</p>
                <p className="text-lg font-semibold">
                  {subscription.nextBillingAmount >=0 ? subscription.nextBillingAmount+' KWD': ''}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Card */}
          {usage && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Resource Usage</h2>

              {usage.warnings && usage.warnings.length > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <p className="font-semibold text-yellow-800">Usage Warnings:</p>
                  <ul className="list-disc list-inside text-yellow-700 text-sm">
                    {usage.warnings.map((warning: string, i: number) => (
                      <li key={i}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-6">
                {/* Storage */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Storage</span>
                    <span className="text-sm text-gray-600">
                      {formatStorageDisplayString(usage.usage?.storageGB || 0, usage.quotas?.storageGB || 0)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        (usage.usage?.storageGB / usage.quotas?.storageGB) >= 0.9 ? 'bg-red-600' :
                        (usage.usage?.storageGB / usage.quotas?.storageGB) >= 0.75 ? 'bg-yellow-600' :
                        'bg-green-600'
                      }`}
                      style={{
                        width: `${Math.min(100, (usage.usage?.storageGB / usage.quotas?.storageGB * 100) || 0)}%`
                      }}
                    />
                  </div>
                </div>

                {/* Queries */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Queries This Month</span>
                    <span className="text-sm text-gray-600">
                      {usage.usage?.queriesThisMonth?.toLocaleString() || 0} /
                      {usage.quotas?.queriesPerMonth?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        (usage.usage?.queriesThisMonth / usage.quotas?.queriesPerMonth) >= 0.9 ? 'bg-red-600' :
                        (usage.usage?.queriesThisMonth / usage.quotas?.queriesPerMonth) >= 0.75 ? 'bg-yellow-600' :
                        'bg-blue-600'
                      }`}
                      style={{
                        width: `${Math.min(100, (usage.usage?.queriesThisMonth / usage.quotas?.queriesPerMonth * 100) || 0)}%`
                      }}
                    />
                  </div>
                </div>

                {/* Users */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Active Users</span>
                    <span className="text-sm text-gray-600">
                      {usage.usage?.activeUsers || 0} / {usage.quotas?.maxUsers === -1 ? 'UNLIMITED' : usage.quotas?.maxUsers || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        (usage.usage?.activeUsers / usage.quotas?.maxUsers) >= 0.9 ? 'bg-red-600' :
                        (usage.usage?.activeUsers / usage.quotas?.maxUsers) >= 0.75 ? 'bg-yellow-600' :
                        'bg-purple-600'
                      }`}
                      style={{
                        width: `${Math.min(100, (usage.usage?.activeUsers / usage.quotas?.maxUsers * 100) || 0)}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Enabled Features</h2>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(subscription.features || {})
                .filter(([, value]) => typeof value === 'boolean')
                .map(([key, value]) => (
                  <div key={key} className={`flex items-center ${value ? '' : 'hidden'}`} >
                    <span className={value ? 'text-green-600' : 'text-gray-400'}>
                      {value ? '✓' : '✗'}
                    </span>
                    <span className="ml-2 text-sm capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {/*<div className="space-y-6">*/}
        {/*  /!* Quick Actions *!/*/}
        {/*  <div className="bg-white rounded-lg shadow p-6">*/}
        {/*    <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>*/}
        {/*    <div className="space-y-2">*/}
        {/*      <button className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">*/}
        {/*        Upgrade Plan*/}
        {/*      </button>*/}
        {/*      <button className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">*/}
        {/*        View Invoices*/}
        {/*      </button>*/}
        {/*      <button className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">*/}
        {/*        Contact Support*/}
        {/*      </button>*/}
        {/*    </div>*/}
        {/*  </div>*/}

        {/*  /!* Billing Info *!/*/}
        {/*  <div className="bg-white rounded-lg shadow p-6">*/}
        {/*    <h2 className="text-lg font-semibold mb-4">Billing Information</h2>*/}
        {/*    <div className="space-y-3 text-sm">*/}
        {/*      <div>*/}
        {/*        <p className="text-gray-600">Next Billing Date</p>*/}
        {/*        <p className="font-semibold">*/}
        {/*          {subscription.nextBillingDate*/}
        {/*            ? new Date(subscription.nextBillingDate).toLocaleDateString('en-US', {*/}
        {/*                year: 'numeric',*/}
        {/*                month: 'long',*/}
        {/*                day: 'numeric'*/}
        {/*              })*/}
        {/*            : '—'}*/}
        {/*        </p>*/}
        {/*      </div>*/}
        {/*      <div>*/}
        {/*        <p className="text-gray-600">Amount Due</p>*/}
        {/*        <p className="font-semibold text-lg">*/}
        {/*          {subscription.nextBillingAmount} KWD*/}
        {/*        </p>*/}
        {/*      </div>*/}
        {/*      {subscription.autoRenew && (*/}
        {/*        <div className="bg-green-50 border border-green-200 rounded p-3 mt-3">*/}
        {/*          <p className="text-green-800 text-xs">*/}
        {/*            ✓ Auto-renewal enabled*/}
        {/*          </p>*/}
        {/*        </div>*/}
        {/*      )}*/}
        {/*    </div>*/}
        {/*  </div>*/}

        {/*  /!* Trial Info *!/*/}
        {/*  {subscription.isTrial && (*/}
        {/*    <div className="bg-blue-50 rounded-lg shadow p-6">*/}
        {/*      <h2 className="text-lg font-semibold mb-4 text-blue-900">Trial Period</h2>*/}
        {/*      <div className="space-y-2 text-sm">*/}
        {/*        <div>*/}
        {/*          <p className="text-blue-700">Days Remaining</p>*/}
        {/*          <p className="text-2xl font-bold text-blue-900">*/}
        {/*            {Math.max(0, Math.ceil(*/}
        {/*              (new Date(subscription.trialEndDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000)*/}
        {/*            ))}*/}
        {/*          </p>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <p className="text-blue-700">Trial Ends</p>*/}
        {/*          <p className="font-semibold text-blue-900">*/}
        {/*            {new Date(subscription.trialEndDate).toLocaleDateString()}*/}
        {/*          </p>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*      <button className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">*/}
        {/*        Convert to Paid*/}
        {/*      </button>*/}
        {/*    </div>*/}
        {/*  )}*/}
        {/*</div>*/}
      </div>
    </div>
  );
}
